import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  Box,
  Flex,
  Heading,
  Text,
  Spinner,
  IconButton,
  useColorModeValue,
  useDisclosure,
  Badge,
  Divider,
  Avatar,
  HStack,
  VStack,
  Grid,
  SimpleGrid,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import {
  MdLocalShipping,
  MdAdd,
  MdDelete,
  MdCheckCircle,
  MdPerson,
} from "react-icons/md";
import { FiTruck, FiUser } from "react-icons/fi";
import { baseUrl } from "../../utils/utils";
import {
  get_dp_vehicle_data,
  get_dp_vehicle_list,
  get_vehicle_driver_list,
} from "../../redux/orderSlice";
import "./DailyRoster.css";
import { get_active_dp_list } from "../../redux/dplistSlice";
import { ArrowLeft, Edit, Trash } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DailyRoster = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = React.useRef();

  const {
    dp_vehicle_data,
    dp_vehicle_list,
    vehicle_driver_list,
    vehicle_loading,
    driver_loading,
  } = useSelector((state) => state.orders);
  const { active_all_dp_list } = useSelector((state) => state.dplist);

  const [rosterData, setRosterData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);

  // Single assignment state for form
  const [assignment, setAssignment] = useState({
    dpid: "",
    vehicle_id: "",
    driver_id: "",
    dpid_error: "",
    vehicle_error: "",
    driver_error: "",
  });

  // Edit assignment state
  const [editAssignment, setEditAssignment] = useState({
    id: "",
    dpid: "",
    vehicle_id: "",
    driver_id: "",
    dpid_error: "",
    vehicle_error: "",
    driver_error: "",
  });

  // Array to store completed assignments
  const [completedAssignments, setCompletedAssignments] = useState([]);

  // Chakra UI colors
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const cardBg = useColorModeValue("white", "gray.800");
  const headingColor = useColorModeValue("gray.700", "gray.200");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const infoCardBg = useColorModeValue("#E6F4FA", "blue.900");
  const vehicleCardBg = useColorModeValue("gray.50", "gray.700");
  const vehicleBorderColor = useColorModeValue("#B3DDF2", "#0175c2");
  const bgColor = useColorModeValue("gray.50", "gray.900");

  const fetchData = async () => {
    try {
      setLoading(true);
      const body = new FormData();
      body.append("action", "list");
      body.append("page", 1);
      body.append("limit", 100);

      const response = await fetch(`${baseUrl}daily_roaster.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();

      if (data.success) {
        setRosterData(data.data);
      } else {
        console.log("Failed:", data.message);
        setRosterData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    dispatch(get_active_dp_list({ page: 1, rows: 1000 }));
  }, [dispatch]);

  // Handle DP selection (Add Modal)
  const handleDPChange = (dpid) => {
    setAssignment({
      ...assignment,
      dpid,
      vehicle_id: "",
      driver_id: "",
      dpid_error: "",
      vehicle_error: "",
      driver_error: "",
    });

    if (dpid) {
      dispatch(get_dp_vehicle_list(dpid));
    }
  };

  // Handle vehicle selection (Add Modal)
  const handleVehicleChange = (vehicle_id) => {
    setAssignment({
      ...assignment,
      vehicle_id,
      driver_id: "",
      vehicle_error: "",
      driver_error: "",
    });

    if (vehicle_id) {
      dispatch(get_vehicle_driver_list(vehicle_id));
    }
  };

  // Handle driver selection (Add Modal)
  const handleDriverChange = (driver_id) => {
    setAssignment({
      ...assignment,
      driver_id,
      driver_error: "",
    });
  };

  // Handle Edit DP selection
  const handleEditDPChange = (dpid) => {
    setEditAssignment({
      ...editAssignment,
      dpid,
      vehicle_id: "",
      driver_id: "",
      dpid_error: "",
      vehicle_error: "",
      driver_error: "",
    });

    if (dpid) {
      dispatch(get_dp_vehicle_data(dpid));
    }
  };

  // Handle Edit vehicle selection
  const handleEditVehicleChange = (vehicle_id) => {
    setEditAssignment({
      ...editAssignment,
      vehicle_id,
      driver_id: "",
      vehicle_error: "",
      driver_error: "",
    });

    if (vehicle_id) {
      dispatch(get_vehicle_driver_list(vehicle_id));
    }
  };

  // Handle Edit driver selection
  const handleEditDriverChange = (driver_id) => {
    setEditAssignment({
      ...editAssignment,
      driver_id,
      driver_error: "",
    });
  };

  // Validate current assignment
  const validateAssignment = () => {
    let isValid = true;
    const errors = { ...assignment };

    if (!assignment.dpid) {
      errors.dpid_error = "Please select a delivery partner";
      isValid = false;
    }
    if (!assignment.vehicle_id) {
      errors.vehicle_error = "Please select a vehicle";
      isValid = false;
    }
    if (!assignment.driver_id) {
      errors.driver_error = "Please select a driver";
      isValid = false;
    }

    setAssignment(errors);
    return isValid;
  };

  // Validate edit assignment
  const validateEditAssignment = () => {
    let isValid = true;
    const errors = { ...editAssignment };

    if (!editAssignment.dpid) {
      errors.dpid_error = "Please select a delivery partner";
      isValid = false;
    }
    if (!editAssignment.vehicle_id) {
      errors.vehicle_error = "Please select a vehicle";
      isValid = false;
    }
    if (!editAssignment.driver_id) {
      errors.driver_error = "Please select a driver";
      isValid = false;
    }

    setEditAssignment(errors);
    return isValid;
  };

  // Add assignment to completed list
  // Add assignment to completed list
  const handleAddToList = () => {
    if (!validateAssignment()) {
      return;
    }

    const dpName =
      active_all_dp_list?.find((item) => item.id === assignment.dpid)?.name ||
      `DP #${assignment.dpid}`;
    const vehicleName =
      dp_vehicle_list?.find((item) => item.vehicle_id === assignment.vehicle_id)
        ?.vehicleno || `Vehicle #${assignment.vehicle_id}`;

    // Find driver name - check both possible field names
    const driver = vehicle_driver_list?.find(
      (item) => (item.driverid || item.id) == assignment.driver_id,
    );
    const driverName =
      driver?.driver_name || driver?.name || `Driver #${assignment.driver_id}`;

    setCompletedAssignments([
      ...completedAssignments,
      {
        dpid: assignment.dpid, // ✅ Only store the ID
        vehicle_id: assignment.vehicle_id, // ✅ Only store the ID
        driver_id: assignment.driver_id, // ✅ Only store the ID (not the whole object)
        dpName, // Store name for display only
        vehicleName, // Store name for display only
        driverName, // Store name for display only
        id: Date.now(), // Temporary ID for the list
      },
    ]);

    setAssignment({
      dpid: "",
      vehicle_id: "",
      driver_id: "",
      dpid_error: "",
      vehicle_error: "",
      driver_error: "",
    });
  };

  // Remove assignment from completed list
  const removeAssignment = (id) => {
    setCompletedAssignments(
      completedAssignments.filter((item) => item.id !== id),
    );
  };

  // Handle create assignments
  const handleCreateAssignments = async () => {
    if (completedAssignments.length === 0) {
      toast({
        title: "Warning",
        description: "Please add at least one assignment",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const body = new FormData();
      body.append("action", "create");

      completedAssignments.forEach((assignment) => {
        body.append("dpid[]", assignment.dpid);
        body.append("vehicle_id[]", assignment.vehicle_id);
        body.append("driver_id[]", assignment.driver_id);
      });

      const response = await fetch(`${baseUrl}daily_roaster.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Success",
          description: data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setAssignment({
          dpid: "",
          vehicle_id: "",
          driver_id: "",
          dpid_error: "",
          vehicle_error: "",
          driver_error: "",
        });
        setCompletedAssignments([]);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error creating roster:", error);
      toast({
        title: "Error",
        description: "Failed to create roster",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Edit Click
  const handleEditClick = (dp, vehicle, driver) => {
    // Use the actual roster ID from the driver object
    const rosterId = driver.id || driver.driver_id;

    setEditAssignment({
      id: rosterId,
      dpid: dp.dpid,
      vehicle_id: vehicle.vehicle_id,
      driver_id: driver.driver_id,
      dpid_error: "",
      vehicle_error: "",
      driver_error: "",
    });

    // Load vehicle and driver lists
    dispatch(get_dp_vehicle_data(dp.dpid));
    dispatch(get_vehicle_driver_list(vehicle.vehicle_id));

    onEditOpen();
  };

  // Handle Update Assignment
  const handleUpdateAssignment = async () => {
    if (!validateEditAssignment()) {
      return;
    }

    try {
      setLoading(true);
      const body = new FormData();
      body.append("action", "update");
      body.append("id", editAssignment.id);
      body.append("dpid", editAssignment.dpid);
      body.append("vehicle_id", editAssignment.vehicle_id);
      body.append("driver_id", editAssignment.driver_id);

      const response = await fetch(`${baseUrl}daily_roaster.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Success",
          description: data.message || "Roster updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onEditClose();
        setEditAssignment({
          id: "",
          dpid: "",
          vehicle_id: "",
          driver_id: "",
          dpid_error: "",
          vehicle_error: "",
          driver_error: "",
        });
        fetchData();
      } else {
        toast({
          title: "Error",
          description: data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating roster:", error);
      toast({
        title: "Error",
        description: "Failed to update roster",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle Delete Click
  const handleDeleteClick = (id) => {
    setDeleteItemId(id);
    onDeleteOpen();
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      setDeleteLoading(true);
      const body = new FormData();
      body.append("action", "delete");
      body.append("id", deleteItemId);

      const response = await fetch(`${baseUrl}daily_roaster.php`, {
        method: "POST",
        body: body,
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: "Success",
          description: data.message || "Roster deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onDeleteClose();
        setDeleteItemId(null);
        fetchData();
      } else {
        toast({
          title: "Error",
          description: data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error deleting roster:", error);
      toast({
        title: "Error",
        description: "Failed to delete roster",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box p={2} bg={bgColor} minH="100vh">
      <Box maxW="1400px" mx="auto">
        {/* Header Section */}
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <button
              onClick={() => navigate(-1)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                color: "#403e3e",
              }}
            >
              <ArrowLeft />
              <p style={{ fontSize: 17, fontWeight: "bold" }}>Back</p>
            </button>
            <Heading size="lg" mb={1} color="#0175c2" mt={2}>
              Daily Roster Management
            </Heading>
            <Text color={textColor} fontSize="sm">
              View and manage delivery partner assignments
            </Text>
          </Box>
          <Button
            leftIcon={<MdAdd size={16} />}
            bg="#0175c2"
            color="white"
            size="md"
            px={4}
            onClick={onOpen}
            boxShadow="md"
            _hover={{
              bg: "#015a94",
              transform: "translateY(-1px)",
              boxShadow: "lg",
            }}
            transition="all 0.3s"
          >
            Add New
          </Button>
        </Flex>

        {/* Stats Cards */}
        <Grid
          templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
          gap={3}
          mb={4}
        >
          <Box
            bg={cardBg}
            p={3}
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex align="center" justify="space-between">
              <Box>
                <Text
                  color={textColor}
                  fontSize="xs"
                  fontWeight="medium"
                  mb={1}
                >
                  Total Partners
                </Text>
                <Heading size="md" color="#0175c2">
                  {rosterData.length}
                </Heading>
              </Box>
              <Box bg="#E6F4FA" p={2} borderRadius="md">
                <MdLocalShipping size={20} color="#0175c2" />
              </Box>
            </Flex>
          </Box>

          <Box
            bg={cardBg}
            p={3}
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex align="center" justify="space-between">
              <Box>
                <Text
                  color={textColor}
                  fontSize="xs"
                  fontWeight="medium"
                  mb={1}
                >
                  Total Vehicles
                </Text>
                <Heading size="md" color="#0175c2">
                  {rosterData.reduce((acc, dp) => acc + dp.vehicles.length, 0)}
                </Heading>
              </Box>
              <Box bg="#E6F4FA" p={2} borderRadius="md">
                <FiTruck size={20} color="#0175c2" />
              </Box>
            </Flex>
          </Box>

          <Box
            bg={cardBg}
            p={3}
            borderRadius="lg"
            boxShadow="md"
            border="1px solid"
            borderColor={borderColor}
          >
            <Flex align="center" justify="space-between">
              <Box>
                <Text
                  color={textColor}
                  fontSize="xs"
                  fontWeight="medium"
                  mb={1}
                >
                  Total Drivers
                </Text>
                <Heading size="md" color="#0175c2">
                  {rosterData.reduce(
                    (acc, dp) =>
                      acc +
                      dp.vehicles.reduce(
                        (vAcc, v) => vAcc + v.drivers.length,
                        0,
                      ),
                    0,
                  )}
                </Heading>
              </Box>
              <Box bg="#E6F4FA" p={2} borderRadius="md">
                <FiUser size={20} color="#0175c2" />
              </Box>
            </Flex>
          </Box>
        </Grid>

        {/* Roster List */}
        {loading ? (
          <Flex justify="center" align="center" minH="300px">
            <Spinner size="lg" color="#0175c2" thickness="3px" />
          </Flex>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
            {rosterData.map((dp, index) => (
              <Box
                key={index}
                bg={cardBg}
                borderRadius="lg"
                overflow="hidden"
                boxShadow="md"
                border="1px solid"
                borderColor={borderColor}
                transition="all 0.3s"
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "lg",
                }}
              >
                {/* Header */}
                <Box bg="#327eb1" p={3} color="white">
                  <Flex align="center" justify="space-between">
                    <HStack spacing={3}>
                      <Avatar
                        name={dp.dp_name}
                        size="sm"
                        bg="white"
                        color="#0175c2"
                        icon={<MdPerson size={18} />}
                      />
                      <Box>
                        <Heading size="sm" mb={0.5}>
                          {dp.dp_name}
                        </Heading>
                      </Box>
                    </HStack>
                    <HStack>
                      <Badge
                        bg="whiteAlpha.300"
                        color="white"
                        px={2}
                        py={1}
                        borderRadius="full"
                        fontSize="xs"
                        fontWeight="bold"
                      >
                        <Flex align="center" gap={1}>
                          <FiTruck size={12} />
                          {dp.vehicles.length} Vehicle
                          {dp.vehicles.length !== 1 ? "s" : ""}
                        </Flex>
                      </Badge>
                    </HStack>
                  </Flex>
                </Box>

                {/* Vehicle Cards */}
                <Box p={3}>
                  <VStack spacing={2} align="stretch">
                    {dp.vehicles.map((vehicle, vIndex) => (
                      <Box
                        key={vIndex}
                        bg={vehicleCardBg}
                        p={3}
                        borderRadius="md"
                        border="2px solid"
                        borderColor={vehicleBorderColor}
                        transition="all 0.2s"
                        _hover={{
                          borderColor: "#0175c2",
                          transform: "scale(1.01)",
                        }}
                      >
                        {/* Vehicle Info */}
                        <Flex align="center" gap={2} mb={2}>
                          <Box
                            bg="#0175c2"
                            p={2}
                            borderRadius="md"
                            color="white"
                          >
                            <FiTruck size={16} />
                          </Box>
                          <Box flex={1}>
                            <Text
                              fontSize="9px"
                              color={textColor}
                              fontWeight="medium"
                              textTransform="uppercase"
                            >
                              Vehicle
                            </Text>
                            <Text
                              fontWeight="bold"
                              fontSize="sm"
                              color={headingColor}
                              letterSpacing="wide"
                            >
                              {vehicle.vehicle_no}
                            </Text>
                          </Box>
                        </Flex>

                        <Divider mb={2} />

                        {/* Drivers */}
                        <VStack spacing={2} align="stretch">
                          {vehicle.drivers.map((driver, dIndex) => (
                            <>
                              <Flex
                                key={dIndex}
                                align="center"
                                gap={2}
                                bg={cardBg}
                                p={2}
                                borderRadius="md"
                                border="1px solid"
                                borderColor={borderColor}
                              >
                                <Box
                                  bg="#E6F4FA"
                                  p={1.5}
                                  borderRadius="full"
                                  color="#0175c2"
                                >
                                  <FiUser size={14} />
                                </Box>
                                <Box flex={1}>
                                  <Text
                                    fontSize="9px"
                                    color={textColor}
                                    fontWeight="medium"
                                  >
                                    Driver
                                  </Text>
                                  <Text
                                    fontWeight="bold"
                                    color={headingColor}
                                    fontSize="sm"
                                  >
                                    {driver.driver_name}
                                  </Text>
                                </Box>
                                {/* <HStack spacing={1}>
                                <Badge
                                  bg="#0175c2"
                                  color="white"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                  fontSize="9px"
                                  cursor="pointer"
                                  onClick={() =>
                                    handleEditClick(dp, vehicle, driver)
                                  }
                                  _hover={{ bg: "#015a94" }}
                                >
                                  <Flex align="center" gap={1}>
                                    <Edit size={10} />
                                    Edit
                                  </Flex>
                                </Badge>
                                <Badge
                                  bg="red.500"
                                  color="white"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                  fontSize="9px"
                                  cursor="pointer"
                                  onClick={() =>
                                    handleDeleteClick(
                                      driver.id || driver.driver_id,
                                    )
                                  }
                                  _hover={{ bg: "red.600" }}
                                >
                                  <Flex align="center" gap={1}>
                                    <Trash size={10} />
                                    Delete
                                  </Flex>
                                </Badge>
                              </HStack> */}
                              </Flex>
                              <HStack spacing={2} justifyContent={"flex-end"}>
                                <Badge
                                  bg="#0175c2"
                                  color="white"
                                  px={3}
                                  py={2}
                                  borderRadius="full"
                                  fontSize="9px"
                                  cursor="pointer"
                                  onClick={() =>
                                    handleEditClick(dp, vehicle, driver)
                                  }
                                  _hover={{ bg: "#015a94" }}
                                >
                                  <Flex align="center" gap={1}>
                                    <Edit size={10} />
                                    Edit
                                  </Flex>
                                </Badge>
                                <Badge
                                  bg="red.500"
                                  color="white"
                                  px={3}
                                  py={2}
                                  borderRadius="full"
                                  fontSize="9px"
                                  cursor="pointer"
                                  onClick={() =>
                                    handleDeleteClick(
                                      driver.id || driver.driver_id,
                                    )
                                  }
                                  _hover={{ bg: "red.600" }}
                                >
                                  <Flex align="center" gap={1}>
                                    <Trash size={10} />
                                    Delete
                                  </Flex>
                                </Badge>
                              </HStack>
                            </>
                          ))}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Box>

      {/* Add Modal */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl" maxH="90vh">
          <ModalHeader
            borderBottom="1px solid"
            borderColor={borderColor}
            bg={cardBg}
            borderTopRadius="xl"
            py={3}
          >
            <Flex align="center" justify="space-between">
              <Flex align="center">
                <Box fontSize="lg" color="#0175c2" mr={2}>
                  <MdLocalShipping />
                </Box>
                <Heading fontSize="md">Add Daily Roster</Heading>
              </Flex>
              {completedAssignments.length > 0 && (
                <Badge
                  colorScheme="green"
                  fontSize="xs"
                  px={2}
                  py={1}
                  borderRadius="full"
                >
                  {completedAssignments.length} Assignment
                  {completedAssignments.length !== 1 ? "s" : ""} Added
                </Badge>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={3} overflowY="auto">
            <Flex flexDirection="column" gap={3} w="full">
              {/* Current Assignment Form */}
              <Box
                p={3}
                borderRadius="lg"
                border="2px solid"
                borderColor="#B3DDF2"
                bg={infoCardBg}
              >
                <Flex align="center" gap={2} mb={3}>
                  <Box color="#0175c2" fontSize="md">
                    <MdAdd />
                  </Box>
                  <Text fontWeight="bold" color="#0175c2" fontSize="sm">
                    New Assignment
                  </Text>
                </Flex>

                <FormControl mb={3}>
                  <FormLabel
                    mb={1}
                    fontWeight="semibold"
                    color={headingColor}
                    fontSize="sm"
                  >
                    Select Delivery Partner
                  </FormLabel>
                  <Select
                    value={assignment.dpid}
                    onChange={(e) => handleDPChange(e.target.value)}
                    placeholder="Choose delivery partner"
                    size="sm"
                    bg="white"
                    borderColor={
                      assignment.dpid_error ? "red.300" : borderColor
                    }
                    _focus={{
                      borderColor: "#0175c2",
                      boxShadow: "0 0 0 1px #0175c2",
                    }}
                  >
                    {active_all_dp_list?.map((item) => (
                      <option key={item?.id} value={item?.id}>
                        {item?.name}
                      </option>
                    ))}
                  </Select>
                  {assignment.dpid_error && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      ⚠️ {assignment.dpid_error}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={3}>
                  <FormLabel
                    mb={1}
                    fontWeight="semibold"
                    color={headingColor}
                    fontSize="sm"
                  >
                    Select Vehicle
                  </FormLabel>
                  <Select
                    value={assignment.vehicle_id}
                    onChange={(e) => handleVehicleChange(e.target.value)}
                    placeholder="Select vehicle"
                    size="sm"
                    bg="white"
                    disabled={!assignment.dpid}
                    borderColor={
                      assignment.vehicle_error ? "red.300" : borderColor
                    }
                    _focus={{
                      borderColor: "#0175c2",
                      boxShadow: "0 0 0 1px #0175c2",
                    }}
                  >
                    {vehicle_loading ? (
                      <option disabled>Loading vehicles...</option>
                    ) : (
                      dp_vehicle_list?.map((item) => (
                        <option key={item.vehicle_id} value={item.vehicle_id}>
                          {item.vehicleno}
                        </option>
                      ))
                    )}
                  </Select>
                  {vehicle_loading && (
                    <Flex align="center" gap={2} mt={1}>
                      <Spinner size="xs" color="#0175c2" />
                      <Text fontSize="xs" color={textColor}>
                        Loading available vehicles...
                      </Text>
                    </Flex>
                  )}
                  {assignment.vehicle_error && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      ⚠️ {assignment.vehicle_error}
                    </Text>
                  )}
                </FormControl>

                <FormControl mb={3}>
                  <FormLabel
                    mb={1}
                    fontWeight="semibold"
                    color={headingColor}
                    fontSize="sm"
                  >
                    Select Driver
                  </FormLabel>
                  <Select
                    value={assignment.driver_id}
                    onChange={(e) => handleDriverChange(e.target.value)}
                    placeholder="Choose driver"
                    size="sm"
                    bg="white"
                    disabled={!assignment.vehicle_id}
                    borderColor={
                      assignment.driver_error ? "red.300" : borderColor
                    }
                    _focus={{
                      borderColor: "#0175c2",
                      boxShadow: "0 0 0 1px #0175c2",
                    }}
                  >
                    {driver_loading ? (
                      <option disabled>Loading drivers...</option>
                    ) : (
                      vehicle_driver_list?.map((item) => (
                        <option
                          key={item.driverid || item.id}
                          value={item.driverid || item.id}
                        >
                          {item.driver_name || item.name}
                        </option>
                      ))
                    )}
                  </Select>
                  {driver_loading && (
                    <Flex align="center" gap={2} mt={1}>
                      <Spinner size="xs" color="#0175c2" />
                      <Text fontSize="xs" color={textColor}>
                        Loading available drivers...
                      </Text>
                    </Flex>
                  )}
                  {assignment.driver_error && (
                    <Text color="red.500" fontSize="xs" mt={1}>
                      ⚠️ {assignment.driver_error}
                    </Text>
                  )}
                </FormControl>

                <Button
                  leftIcon={<MdCheckCircle size={14} />}
                  onClick={handleAddToList}
                  bg="#0175c2"
                  color="white"
                  size="sm"
                  w="full"
                  _hover={{
                    bg: "#015a94",
                  }}
                >
                  Add to List
                </Button>
              </Box>

              {/* Completed Assignments List */}
              {completedAssignments.length > 0 && (
                <>
                  <Divider />
                  <Box>
                    <Flex align="center" gap={2} mb={2}>
                      <Box color="green.500" fontSize="md">
                        <MdCheckCircle />
                      </Box>
                      <Heading size="sm" color={headingColor}>
                        Added Assignments ({completedAssignments.length})
                      </Heading>
                    </Flex>

                    <Flex flexDirection="column" gap={2}>
                      {completedAssignments.map((item) => (
                        <Box
                          key={item.id}
                          p={3}
                          borderRadius="md"
                          bg="white"
                          border="1px solid"
                          borderColor="green.200"
                          boxShadow="sm"
                          position="relative"
                          transition="all 0.2s"
                          _hover={{
                            boxShadow: "md",
                          }}
                        >
                          <IconButton
                            icon={<MdDelete />}
                            colorScheme="red"
                            size="xs"
                            position="absolute"
                            top={1}
                            right={1}
                            onClick={() => removeAssignment(item.id)}
                            aria-label="Delete assignment"
                            variant="ghost"
                          />

                          <Flex gap={3} align="center" pr={8}>
                            <Flex align="center" gap={1.5} flex={1}>
                              <Box color="#0175c2" fontSize="md">
                                <MdLocalShipping />
                              </Box>
                              <Box>
                                <Text
                                  fontSize="9px"
                                  color={textColor}
                                  fontWeight="medium"
                                >
                                  Delivery Partner
                                </Text>
                                <Text
                                  fontWeight="bold"
                                  color={headingColor}
                                  fontSize="xs"
                                >
                                  {item.dpName}
                                </Text>
                              </Box>
                            </Flex>

                            <Flex align="center" gap={1.5} flex={1}>
                              <Box color="#0175c2" fontSize="md">
                                <FiTruck />
                              </Box>
                              <Box>
                                <Text
                                  fontSize="9px"
                                  color={textColor}
                                  fontWeight="medium"
                                >
                                  Vehicle
                                </Text>
                                <Text
                                  fontWeight="bold"
                                  color={headingColor}
                                  fontSize="xs"
                                >
                                  {item.vehicleName}
                                </Text>
                              </Box>
                            </Flex>

                            <Flex align="center" gap={1.5} flex={1}>
                              <Box color="#0175c2" fontSize="md">
                                <FiUser />
                              </Box>
                              <Box>
                                <Text
                                  fontSize="9px"
                                  color={textColor}
                                  fontWeight="medium"
                                >
                                  Driver
                                </Text>
                                <Text
                                  fontWeight="bold"
                                  color={headingColor}
                                  fontSize="xs"
                                >
                                  {item.driverName}
                                </Text>
                              </Box>
                            </Flex>
                          </Flex>
                        </Box>
                      ))}
                    </Flex>
                  </Box>
                </>
              )}
            </Flex>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor={borderColor}
            gap={2}
            py={3}
          >
            <Button
              colorScheme="gray"
              onClick={() => {
                onClose();
                setAssignment({
                  dpid: "",
                  vehicle_id: "",
                  driver_id: "",
                  dpid_error: "",
                  vehicle_error: "",
                  driver_error: "",
                });
                setCompletedAssignments([]);
              }}
              variant="outline"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              bg="#0175c2"
              color="white"
              onClick={handleCreateAssignments}
              isLoading={loading}
              size="sm"
              px={4}
              leftIcon={<MdLocalShipping size={14} />}
              isDisabled={completedAssignments.length === 0}
              _hover={{
                bg: "#015a94",
              }}
            >
              Create Roster ({completedAssignments.length})
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditOpen} onClose={onEditClose} isCentered size="md">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader
            borderBottom="1px solid"
            borderColor={borderColor}
            bg={cardBg}
            borderTopRadius="xl"
            py={3}
          >
            <Flex align="center">
              <Box fontSize="lg" color="#0175c2" mr={2}>
                <Edit size={18} />
              </Box>
              <Heading fontSize="md">Edit Roster Assignment</Heading>
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={4}>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel
                  mb={1}
                  fontWeight="semibold"
                  color={headingColor}
                  fontSize="sm"
                >
                  Select Delivery Partner
                </FormLabel>
                <Select
                  value={editAssignment.dpid}
                  onChange={(e) => handleEditDPChange(e.target.value)}
                  placeholder="Choose delivery partner"
                  size="sm"
                  borderColor={
                    editAssignment.dpid_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "#0175c2",
                    boxShadow: "0 0 0 1px #0175c2",
                  }}
                >
                  {active_all_dp_list?.map((item) => (
                    <option key={item?.id} value={item?.id}>
                      {item?.name}
                    </option>
                  ))}
                </Select>
                {editAssignment.dpid_error && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    ⚠️ {editAssignment.dpid_error}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  mb={1}
                  fontWeight="semibold"
                  color={headingColor}
                  fontSize="sm"
                >
                  Select Vehicle
                </FormLabel>
                <Select
                  value={editAssignment.vehicle_id}
                  onChange={(e) => handleEditVehicleChange(e.target.value)}
                  placeholder="Select vehicle"
                  size="sm"
                  disabled={!editAssignment.dpid}
                  borderColor={
                    editAssignment.vehicle_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "#0175c2",
                    boxShadow: "0 0 0 1px #0175c2",
                  }}
                >
                  {vehicle_loading ? (
                    <option disabled>Loading vehicles...</option>
                  ) : (
                    dp_vehicle_data?.map((item) => (
                      <option key={item.vehicle_id} value={item.vehicle_id}>
                        {item.vehicleno}
                      </option>
                    ))
                  )}
                </Select>
                {editAssignment.vehicle_error && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    ⚠️ {editAssignment.vehicle_error}
                  </Text>
                )}
              </FormControl>

              <FormControl>
                <FormLabel
                  mb={1}
                  fontWeight="semibold"
                  color={headingColor}
                  fontSize="sm"
                >
                  Select Driver
                </FormLabel>
                <Select
                  value={editAssignment.driver_id}
                  onChange={(e) => handleEditDriverChange(e.target.value)}
                  placeholder="Choose driver"
                  size="sm"
                  disabled={!editAssignment.vehicle_id}
                  borderColor={
                    editAssignment.driver_error ? "red.300" : borderColor
                  }
                  _focus={{
                    borderColor: "#0175c2",
                    boxShadow: "0 0 0 1px #0175c2",
                  }}
                >
                  {driver_loading ? (
                    <option disabled>Loading drivers...</option>
                  ) : (
                    vehicle_driver_list?.map((item) => (
                      <option
                        key={item.driverid || item.id}
                        value={item.driverid || item.id}
                      >
                        {item.driver_name || item.name}
                      </option>
                    ))
                  )}
                </Select>
                {editAssignment.driver_error && (
                  <Text color="red.500" fontSize="xs" mt={1}>
                    ⚠️ {editAssignment.driver_error}
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter
            borderTop="1px solid"
            borderColor={borderColor}
            gap={2}
            py={3}
          >
            <Button
              colorScheme="gray"
              onClick={() => {
                onEditClose();
                setEditAssignment({
                  id: "",
                  dpid: "",
                  vehicle_id: "",
                  driver_id: "",
                  dpid_error: "",
                  vehicle_error: "",
                  driver_error: "",
                });
              }}
              variant="outline"
              size="sm"
            >
              Cancel
            </Button>
            <Button
              bg="#0175c2"
              color="white"
              onClick={handleUpdateAssignment}
              isLoading={loading}
              size="sm"
              px={4}
              leftIcon={<Edit size={14} />}
              _hover={{
                bg: "#015a94",
              }}
            >
              Update Roster
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Roster Assignment
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete this roster assignment? This
              action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose} size="sm">
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                size="sm"
                isLoading={deleteLoading}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default DailyRoster;
// import React, { useEffect, useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalBody,
//   ModalFooter,
//   ModalCloseButton,
//   Button,
//   FormControl,
//   FormLabel,
//   Select,
//   Box,
//   Flex,
//   Heading,
//   Text,
//   Spinner,
//   IconButton,
//   useColorModeValue,
//   useDisclosure,
//   Badge,
//   Divider,
//   Avatar,
//   HStack,
//   VStack,
//   Grid,
//   SimpleGrid,
//   useToast,
//   Link,
// } from "@chakra-ui/react";
// import {
//   MdLocalShipping,
//   MdAdd,
//   MdDelete,
//   MdCheckCircle,
//   MdPerson,
// } from "react-icons/md";
// import { FiTruck, FiUser } from "react-icons/fi";
// import { baseUrl } from "../../utils/utils";
// import {
//   get_dp_vehicle_list,
//   get_vehicle_driver_list,
// } from "../../redux/orderSlice";
// import "./DailyRoster.css";
// import { get_active_dp_list } from "../../redux/dplistSlice";
// import { ArrowLeft, Delete, DeleteIcon, Edit, Trash } from "lucide-react";
// import { useNavigate } from "react-router-dom";

// const DailyRoster = () => {
//   const toast = useToast();
//   const navigate = useNavigate();
//   const dispatch = useDispatch();
//   const { isOpen, onOpen, onClose } = useDisclosure();

//   const {
//     dp_vehicle_list,
//     vehicle_driver_list,
//     vehicle_loading,
//     driver_loading,
//   } = useSelector((state) => state.orders);
//   const { active_all_dp_list } = useSelector((state) => state.dplist);

//   const [rosterData, setRosterData] = useState([]);
//   const [loading, setLoading] = useState(false);

//   // Single assignment state for form
//   const [assignment, setAssignment] = useState({
//     dpid: "",
//     vehicle_id: "",
//     driver_id: "",
//     dpid_error: "",
//     vehicle_error: "",
//     driver_error: "",
//   });

//   // Array to store completed assignments
//   const [completedAssignments, setCompletedAssignments] = useState([]);

//   // Chakra UI colors - MOVED ALL HOOKS TO TOP LEVEL
//   const borderColor = useColorModeValue("gray.200", "gray.600");
//   const cardBg = useColorModeValue("white", "gray.800");
//   const headingColor = useColorModeValue("gray.700", "gray.200");
//   const textColor = useColorModeValue("gray.600", "gray.400");
//   const infoCardBg = useColorModeValue("#E6F4FA", "blue.900");
//   const vehicleCardBg = useColorModeValue("gray.50", "gray.700");
//   const vehicleBorderColor = useColorModeValue("#B3DDF2", "#0175c2");
//   const bgColor = useColorModeValue("gray.50", "gray.900");

//   const fetchData = async () => {
//     try {
//       setLoading(true);
//       const body = new FormData();
//       body.append("action", "list");
//       body.append("page", 1);
//       body.append("limit", 10);

//       const response = await fetch(`${baseUrl}daily_roaster.php`, {
//         method: "POST",
//         body: body,
//       });

//       const data = await response.json();

//       if (data.success) {
//         setRosterData(data.data);
//       } else {
//         console.log("Failed:", data.message);
//       }
//     } catch (error) {
//       console.error("Error fetching data:", error);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchData();
//     dispatch(get_active_dp_list({ page: 1, rows: 100 }));
//   }, [dispatch]);

//   // Handle DP selection
//   const handleDPChange = (dpid) => {
//     setAssignment({
//       ...assignment,
//       dpid,
//       vehicle_id: "",
//       driver_id: "",
//       dpid_error: "",
//       vehicle_error: "",
//       driver_error: "",
//     });

//     if (dpid) {
//       dispatch(get_dp_vehicle_list(dpid));
//     }
//   };

//   // Handle vehicle selection
//   const handleVehicleChange = (vehicle_id) => {
//     setAssignment({
//       ...assignment,
//       vehicle_id,
//       driver_id: "",
//       vehicle_error: "",
//       driver_error: "",
//     });

//     if (vehicle_id) {
//       dispatch(get_vehicle_driver_list(vehicle_id));
//     }
//   };

//   // Handle driver selection
//   const handleDriverChange = (driver_id) => {
//     setAssignment({
//       ...assignment,
//       driver_id,
//       driver_error: "",
//     });
//   };

//   // Validate current assignment
//   const validateAssignment = () => {
//     let isValid = true;
//     const errors = { ...assignment };

//     if (!assignment.dpid) {
//       errors.dpid_error = "Please select a delivery partner";
//       isValid = false;
//     }
//     if (!assignment.vehicle_id) {
//       errors.vehicle_error = "Please select a vehicle";
//       isValid = false;
//     }
//     if (!assignment.driver_id) {
//       errors.driver_error = "Please select a driver";
//       isValid = false;
//     }

//     setAssignment(errors);
//     return isValid;
//   };

//   // Add assignment to completed list
//   const handleAddToList = () => {
//     if (!validateAssignment()) {
//       return;
//     }

//     const dpName =
//       active_all_dp_list?.find((item) => item.id === assignment.dpid)?.name ||
//       `DP #${assignment.dpid}`;
//     const vehicleName =
//       dp_vehicle_list?.find((item) => item.vehicle_id === assignment.vehicle_id)
//         ?.vehicleno || `Vehicle #${assignment.vehicle_id}`;
//     const driverName =
//       vehicle_driver_list?.find((item) => item.id === assignment.driver_id)
//         ?.name || `Driver #${assignment.driver_id}`;

//     setCompletedAssignments([
//       ...completedAssignments,
//       {
//         ...assignment,
//         dpName,
//         vehicleName,
//         driverName,
//         id: Date.now(),
//       },
//     ]);

//     setAssignment({
//       dpid: "",
//       vehicle_id: "",
//       driver_id: "",
//       dpid_error: "",
//       vehicle_error: "",
//       driver_error: "",
//     });
//   };

//   // Remove assignment from completed list
//   const removeAssignment = (id) => {
//     setCompletedAssignments(
//       completedAssignments.filter((item) => item.id !== id),
//     );
//   };

//   // Handle create assignments
//   const handleCreateAssignments = async () => {
//     if (completedAssignments.length === 0) {
//       alert("Please add at least one assignment");
//       return;
//     }

//     try {
//       setLoading(true);
//       const body = new FormData();
//       body.append("action", "create");

//       completedAssignments.forEach((assignment) => {
//         body.append("dpid[]", assignment.dpid);
//         body.append("vehicle_id[]", assignment.vehicle_id);
//         body.append("driver_id[]", assignment.driver_id);
//       });

//       const response = await fetch(`${baseUrl}daily_roaster.php`, {
//         method: "POST",
//         body: body,
//       });

//       const data = await response.json();

//       if (data.success) {
//         toast({
//           title: "Success",
//           description: data.message,
//           status: "success",
//           duration: 3000,
//           isClosable: true,
//         });
//         onClose();
//         setAssignment({
//           dpid: "",
//           vehicle_id: "",
//           driver_id: "",
//           dpid_error: "",
//           vehicle_error: "",
//           driver_error: "",
//         });
//         setCompletedAssignments([]);
//         fetchData();
//       } else {
//         toast({
//           title: "Error",
//           description: data.message,
//           status: "error",
//           duration: 3000,
//           isClosable: true,
//         });
//       }
//     } catch (error) {
//       console.error("Error creating roster:", error);
//       alert("Error creating roster");
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <Box p={2} bg={bgColor} minH="100vh">
//       <Box maxW="1400px" mx="auto">
//         {/* Header Section */}
//         <Flex justify="space-between" align="center" mb={4}>
//           <Box>
//             <button
//               onClick={() => navigate(-1)}
//               style={{
//                 display: "flex",
//                 alignItems: "center",
//                 gap: 5,
//                 color: "#403e3e",
//               }}
//             >
//               <ArrowLeft />{" "}
//               <p
//                 style={{
//                   fontSize: 17,
//                   fontWeight: "bold",
//                 }}
//               >
//                 Back
//               </p>
//             </button>
//             <Heading size="lg" mb={1} color="#0175c2" mt={2}>
//               Daily Roster Management
//             </Heading>
//             <Text color={textColor} fontSize="sm">
//               View and manage delivery partner assignments
//             </Text>
//           </Box>
//           <Button
//             leftIcon={<MdAdd size={16} />}
//             bg="#0175c2"
//             color="white"
//             size="md"
//             px={4}
//             onClick={onOpen}
//             boxShadow="md"
//             _hover={{
//               bg: "#015a94",
//               transform: "translateY(-1px)",
//               boxShadow: "lg",
//             }}
//             transition="all 0.3s"
//           >
//             Add New
//           </Button>
//         </Flex>

//         {/* Stats Cards */}
//         <Grid
//           templateColumns="repeat(auto-fit, minmax(200px, 1fr))"
//           gap={3}
//           mb={4}
//         >
//           <Box
//             bg={cardBg}
//             p={3}
//             borderRadius="lg"
//             boxShadow="md"
//             border="1px solid"
//             borderColor={borderColor}
//           >
//             <Flex align="center" justify="space-between">
//               <Box>
//                 <Text
//                   color={textColor}
//                   fontSize="xs"
//                   fontWeight="medium"
//                   mb={1}
//                 >
//                   Total Partners
//                 </Text>
//                 <Heading size="md" color="#0175c2">
//                   {rosterData.length}
//                 </Heading>
//               </Box>
//               <Box bg="#E6F4FA" p={2} borderRadius="md">
//                 <MdLocalShipping size={20} color="#0175c2" />
//               </Box>
//             </Flex>
//           </Box>

//           <Box
//             bg={cardBg}
//             p={3}
//             borderRadius="lg"
//             boxShadow="md"
//             border="1px solid"
//             borderColor={borderColor}
//           >
//             <Flex align="center" justify="space-between">
//               <Box>
//                 <Text
//                   color={textColor}
//                   fontSize="xs"
//                   fontWeight="medium"
//                   mb={1}
//                 >
//                   Total Vehicles
//                 </Text>
//                 <Heading size="md" color="#0175c2">
//                   {rosterData.reduce((acc, dp) => acc + dp.vehicles.length, 0)}
//                 </Heading>
//               </Box>
//               <Box bg="#E6F4FA" p={2} borderRadius="md">
//                 <FiTruck size={20} color="#0175c2" />
//               </Box>
//             </Flex>
//           </Box>

//           <Box
//             bg={cardBg}
//             p={3}
//             borderRadius="lg"
//             boxShadow="md"
//             border="1px solid"
//             borderColor={borderColor}
//           >
//             <Flex align="center" justify="space-between">
//               <Box>
//                 <Text
//                   color={textColor}
//                   fontSize="xs"
//                   fontWeight="medium"
//                   mb={1}
//                 >
//                   Total Drivers
//                 </Text>
//                 <Heading size="md" color="#0175c2">
//                   {rosterData.reduce(
//                     (acc, dp) =>
//                       acc +
//                       dp.vehicles.reduce(
//                         (vAcc, v) => vAcc + v.drivers.length,
//                         0,
//                       ),
//                     0,
//                   )}
//                 </Heading>
//               </Box>
//               <Box bg="#E6F4FA" p={2} borderRadius="md">
//                 <FiUser size={20} color="#0175c2" />
//               </Box>
//             </Flex>
//           </Box>
//         </Grid>

//         {/* Roster List - Grid of 2 */}
//         {loading ? (
//           <Flex justify="center" align="center" minH="300px">
//             <Spinner size="lg" color="#0175c2" thickness="3px" />
//           </Flex>
//         ) : (
//           <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
//             {rosterData.map((dp, index) => (
//               <Box
//                 key={index}
//                 bg={cardBg}
//                 borderRadius="lg"
//                 overflow="hidden"
//                 boxShadow="md"
//                 border="1px solid"
//                 borderColor={borderColor}
//                 transition="all 0.3s"
//                 _hover={{
//                   transform: "translateY(-2px)",
//                   boxShadow: "lg",
//                 }}
//               >
//                 {/* Header with Solid Color */}
//                 <Box bg="#327eb1" p={3} color="white">
//                   <Flex align="center" justify="space-between">
//                     <HStack spacing={3}>
//                       <Avatar
//                         name={dp.dp_name}
//                         size="sm"
//                         bg="white"
//                         color="#0175c2"
//                         icon={<MdPerson size={18} />}
//                       />
//                       <Box>
//                         <Heading size="sm" mb={0.5}>
//                           {dp.dp_name}
//                         </Heading>
//                         {/* <Text fontSize="xs" opacity={0.9}>
//                           Partner ID: #{dp.dpid}
//                         </Text> */}
//                       </Box>
//                     </HStack>
//                     <HStack>
//                       <Badge
//                         bg="whiteAlpha.300"
//                         color="white"
//                         px={2}
//                         py={1}
//                         borderRadius="full"
//                         fontSize="xs"
//                         fontWeight="bold"
//                       >
//                         <Flex align="center" gap={1}>
//                           <FiTruck size={12} />
//                           {dp.vehicles.length} Vehicle
//                           {dp.vehicles.length !== 1 ? "s" : ""}
//                         </Flex>
//                       </Badge>

//                       <Badge
//                         bg="#0175c2"
//                         color="white"
//                         px={2}
//                         py={1}
//                         borderRadius="full"
//                         fontSize="xs"
//                         fontWeight="bold"
//                         cursor={"pointer"}
//                       >
//                         <Flex align="center" gap={1}>
//                           <Edit size={12} />
//                           Edit
//                         </Flex>
//                       </Badge>
//                       <Badge
//                         bg="red"
//                         color="white"
//                         px={2}
//                         py={1}
//                         borderRadius="full"
//                         fontSize="xs"
//                         fontWeight="bold"
//                         cursor={"pointer"}
//                       >
//                         <Flex align="center" gap={1}>
//                           <Trash size={12} />
//                           Delete
//                         </Flex>
//                       </Badge>
//                     </HStack>
//                   </Flex>
//                 </Box>

//                 {/* Vehicle Cards */}
//                 <Box p={3}>
//                   <VStack spacing={2} align="stretch">
//                     {dp.vehicles.map((vehicle, vIndex) => (
//                       <Box
//                         key={vIndex}
//                         bg={vehicleCardBg}
//                         p={3}
//                         borderRadius="md"
//                         border="2px solid"
//                         borderColor={vehicleBorderColor}
//                         transition="all 0.2s"
//                         _hover={{
//                           borderColor: "#0175c2",
//                           transform: "scale(1.01)",
//                         }}
//                       >
//                         {/* Vehicle Info */}
//                         <Flex align="center" gap={2} mb={2}>
//                           <Box
//                             bg="#0175c2"
//                             p={2}
//                             borderRadius="md"
//                             color="white"
//                           >
//                             <FiTruck size={16} />
//                           </Box>
//                           <Box flex={1}>
//                             <Text
//                               fontSize="9px"
//                               color={textColor}
//                               fontWeight="medium"
//                               textTransform="uppercase"
//                             >
//                               Vehicle
//                             </Text>
//                             <Text
//                               fontWeight="bold"
//                               fontSize="sm"
//                               color={headingColor}
//                               letterSpacing="wide"
//                             >
//                               {vehicle.vehicle_no}
//                             </Text>
//                           </Box>
//                         </Flex>

//                         <Divider mb={2} />

//                         {/* Drivers */}
//                         <VStack spacing={2} align="stretch">
//                           {vehicle.drivers.map((driver, dIndex) => (
//                             <Flex
//                               key={dIndex}
//                               align="center"
//                               gap={2}
//                               bg={cardBg}
//                               p={2}
//                               borderRadius="md"
//                               border="1px solid"
//                               borderColor={borderColor}
//                             >
//                               <Box
//                                 bg="#E6F4FA"
//                                 p={1.5}
//                                 borderRadius="full"
//                                 color="#0175c2"
//                               >
//                                 <FiUser size={14} />
//                               </Box>
//                               <Box flex={1}>
//                                 <Text
//                                   fontSize="9px"
//                                   color={textColor}
//                                   fontWeight="medium"
//                                 >
//                                   Driver
//                                 </Text>
//                                 <Text
//                                   fontWeight="bold"
//                                   color={headingColor}
//                                   fontSize="sm"
//                                 >
//                                   {driver.driver_name}
//                                 </Text>
//                               </Box>
//                               <Badge
//                                 bg="#0175c2"
//                                 color="white"
//                                 px={2}
//                                 py={0.5}
//                                 borderRadius="full"
//                                 fontSize="9px"
//                               >
//                                 Active
//                               </Badge>
//                             </Flex>
//                           ))}
//                         </VStack>
//                       </Box>
//                     ))}
//                   </VStack>
//                 </Box>
//               </Box>
//             ))}
//           </SimpleGrid>
//         )}
//       </Box>

//       {/* Add Modal */}
//       <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
//         <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
//         <ModalContent borderRadius="xl" maxH="90vh">
//           <ModalHeader
//             borderBottom="1px solid"
//             borderColor={borderColor}
//             bg={cardBg}
//             borderTopRadius="xl"
//             py={3}
//           >
//             <Flex align="center" justify="space-between">
//               <Flex align="center">
//                 <Box fontSize="lg" color="#0175c2" mr={2}>
//                   <MdLocalShipping />
//                 </Box>
//                 <Heading fontSize="md">Add Daily Roster</Heading>
//               </Flex>
//               {completedAssignments.length > 0 && (
//                 <Badge
//                   colorScheme="green"
//                   fontSize="xs"
//                   px={2}
//                   py={1}
//                   borderRadius="full"
//                 >
//                   {completedAssignments.length} Assignment
//                   {completedAssignments.length !== 1 ? "s" : ""} Added
//                 </Badge>
//               )}
//             </Flex>
//           </ModalHeader>
//           <ModalCloseButton />

//           <ModalBody py={3} overflowY="auto">
//             <Flex flexDirection="column" gap={3} w="full">
//               {/* Current Assignment Form */}
//               <Box
//                 p={3}
//                 borderRadius="lg"
//                 border="2px solid"
//                 borderColor="#B3DDF2"
//                 bg={infoCardBg}
//               >
//                 <Flex align="center" gap={2} mb={3}>
//                   <Box color="#0175c2" fontSize="md">
//                     <MdAdd />
//                   </Box>
//                   <Text fontWeight="bold" color="#0175c2" fontSize="sm">
//                     New Assignment
//                   </Text>
//                 </Flex>

//                 <FormControl mb={3}>
//                   <FormLabel
//                     mb={1}
//                     fontWeight="semibold"
//                     color={headingColor}
//                     fontSize="sm"
//                   >
//                     Select Delivery Partner
//                   </FormLabel>
//                   <Select
//                     value={assignment.dpid}
//                     onChange={(e) => handleDPChange(e.target.value)}
//                     placeholder="Choose delivery partner"
//                     size="sm"
//                     bg="white"
//                     borderColor={
//                       assignment.dpid_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "#0175c2",
//                       boxShadow: "0 0 0 1px #0175c2",
//                     }}
//                   >
//                     {active_all_dp_list?.map((item) => (
//                       <option key={item?.id} value={item?.id}>
//                         {item?.name}
//                       </option>
//                     ))}
//                   </Select>
//                   {assignment.dpid_error && (
//                     <Text color="red.500" fontSize="xs" mt={1}>
//                       ⚠️ {assignment.dpid_error}
//                     </Text>
//                   )}
//                 </FormControl>

//                 <FormControl mb={3}>
//                   <FormLabel
//                     mb={1}
//                     fontWeight="semibold"
//                     color={headingColor}
//                     fontSize="sm"
//                   >
//                     Select Vehicle
//                   </FormLabel>
//                   <Select
//                     value={assignment.vehicle_id}
//                     onChange={(e) => handleVehicleChange(e.target.value)}
//                     placeholder="Select vehicle"
//                     size="sm"
//                     bg="white"
//                     disabled={!assignment.dpid}
//                     borderColor={
//                       assignment.vehicle_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "#0175c2",
//                       boxShadow: "0 0 0 1px #0175c2",
//                     }}
//                   >
//                     {vehicle_loading ? (
//                       <option disabled>Loading vehicles...</option>
//                     ) : (
//                       dp_vehicle_list?.map((item) => (
//                         <option key={item.vehicle_id} value={item.vehicle_id}>
//                           {item.vehicleno}
//                         </option>
//                       ))
//                     )}
//                   </Select>
//                   {vehicle_loading && (
//                     <Flex align="center" gap={2} mt={1}>
//                       <Spinner size="xs" color="#0175c2" />
//                       <Text fontSize="xs" color={textColor}>
//                         Loading available vehicles...
//                       </Text>
//                     </Flex>
//                   )}
//                   {assignment.vehicle_error && (
//                     <Text color="red.500" fontSize="xs" mt={1}>
//                       ⚠️ {assignment.vehicle_error}
//                     </Text>
//                   )}
//                 </FormControl>

//                 <FormControl mb={3}>
//                   <FormLabel
//                     mb={1}
//                     fontWeight="semibold"
//                     color={headingColor}
//                     fontSize="sm"
//                   >
//                     Select Driver
//                   </FormLabel>
//                   <Select
//                     value={assignment.driver_id}
//                     onChange={(e) => handleDriverChange(e.target.value)}
//                     placeholder="Choose driver"
//                     size="sm"
//                     bg="white"
//                     disabled={!assignment.vehicle_id}
//                     borderColor={
//                       assignment.driver_error ? "red.300" : borderColor
//                     }
//                     _focus={{
//                       borderColor: "#0175c2",
//                       boxShadow: "0 0 0 1px #0175c2",
//                     }}
//                   >
//                     {driver_loading ? (
//                       <option disabled>Loading drivers...</option>
//                     ) : (
//                       vehicle_driver_list?.map((item) => (
//                         <option key={item.id} value={item.id}>
//                           {item.name}
//                         </option>
//                       ))
//                     )}
//                   </Select>
//                   {driver_loading && (
//                     <Flex align="center" gap={2} mt={1}>
//                       <Spinner size="xs" color="#0175c2" />
//                       <Text fontSize="xs" color={textColor}>
//                         Loading available drivers...
//                       </Text>
//                     </Flex>
//                   )}
//                   {assignment.driver_error && (
//                     <Text color="red.500" fontSize="xs" mt={1}>
//                       ⚠️ {assignment.driver_error}
//                     </Text>
//                   )}
//                 </FormControl>

//                 <Button
//                   leftIcon={<MdCheckCircle size={14} />}
//                   onClick={handleAddToList}
//                   bg="#0175c2"
//                   color="white"
//                   size="sm"
//                   w="full"
//                   _hover={{
//                     bg: "#015a94",
//                   }}
//                 >
//                   Add to List
//                 </Button>
//               </Box>

//               {/* Completed Assignments List */}
//               {completedAssignments.length > 0 && (
//                 <>
//                   <Divider />
//                   <Box>
//                     <Flex align="center" gap={2} mb={2}>
//                       <Box color="green.500" fontSize="md">
//                         <MdCheckCircle />
//                       </Box>
//                       <Heading size="sm" color={headingColor}>
//                         Added Assignments ({completedAssignments.length})
//                       </Heading>
//                     </Flex>

//                     <Flex flexDirection="column" gap={2}>
//                       {completedAssignments.map((item) => (
//                         <Box
//                           key={item.id}
//                           p={3}
//                           borderRadius="md"
//                           bg="white"
//                           border="1px solid"
//                           borderColor="green.200"
//                           boxShadow="sm"
//                           position="relative"
//                           transition="all 0.2s"
//                           _hover={{
//                             boxShadow: "md",
//                           }}
//                         >
//                           <IconButton
//                             icon={<MdDelete />}
//                             colorScheme="red"
//                             size="xs"
//                             position="absolute"
//                             top={1}
//                             right={1}
//                             onClick={() => removeAssignment(item.id)}
//                             aria-label="Delete assignment"
//                             variant="ghost"
//                           />

//                           <Flex gap={3} align="center" pr={8}>
//                             <Flex align="center" gap={1.5} flex={1}>
//                               <Box color="#0175c2" fontSize="md">
//                                 <MdLocalShipping />
//                               </Box>
//                               <Box>
//                                 <Text
//                                   fontSize="9px"
//                                   color={textColor}
//                                   fontWeight="medium"
//                                 >
//                                   Delivery Partner
//                                 </Text>
//                                 <Text
//                                   fontWeight="bold"
//                                   color={headingColor}
//                                   fontSize="xs"
//                                 >
//                                   {item.dpName}
//                                 </Text>
//                               </Box>
//                             </Flex>

//                             <Flex align="center" gap={1.5} flex={1}>
//                               <Box color="#0175c2" fontSize="md">
//                                 <FiTruck />
//                               </Box>
//                               <Box>
//                                 <Text
//                                   fontSize="9px"
//                                   color={textColor}
//                                   fontWeight="medium"
//                                 >
//                                   Vehicle
//                                 </Text>
//                                 <Text
//                                   fontWeight="bold"
//                                   color={headingColor}
//                                   fontSize="xs"
//                                 >
//                                   {item.vehicleName}
//                                 </Text>
//                               </Box>
//                             </Flex>

//                             <Flex align="center" gap={1.5} flex={1}>
//                               <Box color="#0175c2" fontSize="md">
//                                 <FiUser />
//                               </Box>
//                               <Box>
//                                 <Text
//                                   fontSize="9px"
//                                   color={textColor}
//                                   fontWeight="medium"
//                                 >
//                                   Driver
//                                 </Text>
//                                 <Text
//                                   fontWeight="bold"
//                                   color={headingColor}
//                                   fontSize="xs"
//                                 >
//                                   {item.driverName}
//                                 </Text>
//                               </Box>
//                             </Flex>
//                           </Flex>
//                         </Box>
//                       ))}
//                     </Flex>
//                   </Box>
//                 </>
//               )}
//             </Flex>
//           </ModalBody>

//           <ModalFooter
//             borderTop="1px solid"
//             borderColor={borderColor}
//             gap={2}
//             py={3}
//           >
//             <Button
//               colorScheme="gray"
//               onClick={() => {
//                 onClose();
//                 setAssignment({
//                   dpid: "",
//                   vehicle_id: "",
//                   driver_id: "",
//                   dpid_error: "",
//                   vehicle_error: "",
//                   driver_error: "",
//                 });
//                 setCompletedAssignments([]);
//               }}
//               variant="outline"
//               size="sm"
//             >
//               Cancel
//             </Button>
//             <Button
//               bg="#0175c2"
//               color="white"
//               onClick={handleCreateAssignments}
//               isLoading={loading}
//               size="sm"
//               px={4}
//               leftIcon={<MdLocalShipping size={14} />}
//               isDisabled={completedAssignments.length === 0}
//               _hover={{
//                 bg: "#015a94",
//               }}
//             >
//               Create Roster ({completedAssignments.length})
//             </Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//     </Box>
//   );
// };

// export default DailyRoster;
